/**
 * Shared error helpers for network-failure detection and Sentry hygiene.
 *
 * Two distinct failure shapes show up in production:
 *  1. A raw fetch rejection — a `TypeError` whose message is the browser's
 *     network-failure string ("Load failed" on Safari, "Failed to fetch" on
 *     Chrome, etc.). Seen in `apiService.invokeEdgeFunction`.
 *  2. A supabase-js wrapped error — a plain object
 *     `{ code: '', details: '', hint: '', message: 'TypeError: Load failed' }`
 *     thrown from `supabase.from(...).select()` when the underlying fetch dies.
 *
 * Both are the same real-world event (a flaky mobile connection / tab
 * backgrounded mid-request) and should be treated as transient, not captured
 * to Sentry as hard errors.
 */

// Substrings that identify a transient network-layer failure across engines.
const TRANSIENT_NETWORK_PATTERNS = [
  'Load failed', // Safari / WebKit
  'Failed to fetch', // Chrome / Blink
  'NetworkError when attempting to fetch resource', // Firefox
  'Network request failed', // React Native
  'The network connection was lost', // iOS URLSession
  'The request timed out', // iOS URLSession timeout
];

/**
 * True when `error` is a transient network failure, regardless of whether it
 * arrived as a real `TypeError` or as a supabase-js plain error object.
 *
 * @param {unknown} error
 * @returns {boolean}
 */
export function isTransientNetworkError(error) {
  if (!error) return false;
  const message = typeof error === 'string' ? error : error.message;
  if (!message || typeof message !== 'string') return false;
  return TRANSIENT_NETWORK_PATTERNS.some((pattern) => message.includes(pattern));
}

/**
 * Normalise an arbitrary thrown value into a real `Error` instance.
 *
 * Sentry cannot extract a title or stack from a plain object, so it buckets
 * every supabase-style `{code, details, hint, message}` throw under the single
 * useless group "Object captured as exception with keys: ...". Wrapping in a
 * real Error restores per-message grouping while preserving the PostgrestError
 * metadata for debugging.
 *
 * @param {unknown} value
 * @returns {Error}
 */
export function toError(value) {
  if (value instanceof Error) return value;
  if (value && typeof value === 'object') {
    const err = new Error(value.message || value.error || 'Unknown error');
    if (value.code) err.code = value.code;
    if (value.details) err.details = value.details;
    if (value.hint) err.hint = value.hint;
    return err;
  }
  return new Error(typeof value === 'string' ? value : 'Unknown error');
}
